import { useEffect, useMemo, useState }           from "react";
import { Grid, Icon, Typography }                 from "@gupshup-frontend/gupshup-ts-components";

import Tooltip                                    from "./Tooltip";
import Header                                     from "./Header";
import Passkey                                    from "./Passkey";
import Content                                    from "./Content";
import TimeStamp                                  from "./TimeStamp";
import ButtonPanel                                from "./ButtonPanel";
import OrderDetails                               from "./OrderDetails";
import LimitedTimeOffer                           from "./LimitedTimeOffer";
import CarouselCardPanel                          from "./CarouselCardPanel";
import CallPermissionRequest                      from "./CallPermissionRequest";
import {
  PREVIEW_REGEX,
  PREVIEW_ICONS,
  TEMPLATE_TYPE,
  CATEGORY_TYPES,
  MAX_BUTTON_INDEX,
  BUTTON_LIST_TYPES,
  DEFAULT_CONATINER_META,
  CALL_PERMISSION_BUTTON,
  TEMPLATE_PREVIEW_DATA_TESTID,
  // Vendored from the old aliased constants/icons and constants/dataTestId modules
  // (see constant.js).
  TEMPLATE_ICONS,
  createTemplateTestId,
}                                                 from "./constant";
import PayNowPanel                                from "./PayNowPanel";
import styles                                     from "./style.module.css";

const WhatsAppPreview = ({ config }) => {

  const [ isToggledPreview, setIsToggledPreview ] = useState(false);

  const {
    width,
    height,
    timezone,
    mediaUrl,
    variables,
    showHeading,
    headingName,
    templateObj,
    isInPreview,
    card_variables,
    isScrollOnHover,
    isMediaUrlPresent,
    bypassMediaUrlCheck,
    ltoOfferCodeExpireIn,
    hidePreview,
    // Change #1: optional validateMediaUrl function, threaded down to Header /
    // OrderDetails / CarouselCardPanel. Defaults to trusting the url outright when the
    // caller doesn't supply one — same as what bypassMediaUrlCheck already does at
    // each of those components.
    validateMediaUrl = async (mediaUrl) => mediaUrl,
  } = config;

  // Dropped from the source: `ctmTemplateObj` and the mapCtmTemplateData(ctmTemplateObj)
  // branch that built `templateObj` from it. index.js (the wrapper this panel
  // is mounted through) never sets ctmTemplateObj; CTM (Central Template Manager) is an
  // unrelated product surface from Bloomreach's; and the mapper is a ~750-line function
  // with its own dependency chain, including a second zustand store. `templateObj` is
  // now always just `config.templateObj` — see port report.

  const {
    data,
    category,
    templateType,
    containerMeta : meta
  } = templateObj;

  useEffect(() => {
    setIsToggledPreview(false);
  },[config]);

  const modifyWithVariables = (content = "", variables = []) => {
    const match = content.match(PREVIEW_REGEX.DYNAMIC_VARIABLE_REGEX) || [];
    match.forEach((e) => {
      content = content.replace(e, variables[0] || "") || "";
      variables.length > 0 && variables.shift();
    });

    return content;
  };

  const modifyButtonsWithVariables = (buttons = [], variables = []) => {
    if(variables.length === 0) {
      return buttons;
    }
    return buttons.map((btn) => {
      if ((btn.type === BUTTON_LIST_TYPES.URL
        || btn.type === BUTTON_LIST_TYPES.PAY_NOW
      ) && btn?.url?.includes(PREVIEW_REGEX.FIRST_VARIABLES) && variables?.length > 0) {
        const example = [btn.url.replace(PREVIEW_REGEX.FIRST_VARIABLES, variables[0] || "") || ''];
        variables.shift();
        return { ...btn, example };
      }
      return btn;
    });
  };

  const updatePreviewVariables = (meta) => {
    /*update containerMeta with provided mediaUrl*/
    if(isMediaUrlPresent || mediaUrl) {
      meta.mediaUrl = mediaUrl;
    }
    /*update containerMeta with provided variables*/
    if(variables) {
      const __variables = [...variables];
      if(meta?.header?.includes(PREVIEW_REGEX.FIRST_VARIABLES) && __variables?.length > 0) {
        meta["sampleHeader"] = modifyWithVariables(meta.header, __variables);
      }
      if(__variables?.length > 0) {
        meta["sampleText"] = modifyWithVariables(meta.data, __variables);
      }
      meta.buttons = modifyButtonsWithVariables(meta.buttons, __variables);
    }
    /*update carousel cards with provided variables*/
    const __card_variables = [...card_variables || []];
    __card_variables?.map((e, index) => {
      const card = meta?.cards?.[index] || null;
      if(card) {
        card.mediaUrl = e?.mediaURL || "";
        if(e?.variables && e.variables.length > 0) {
          card.sampleText = modifyWithVariables(card.body, e.variables);
        }
        card.buttons = modifyButtonsWithVariables(card.buttons, e.variables);
      }
    });
    /*update ltoOfferCodeExpireIn with the provided ones*/
    if(ltoOfferCodeExpireIn && meta?.limitedTimeOffer) {
      meta.limitedTimeOffer.ltoOfferCodeExpireIn = ltoOfferCodeExpireIn || "";
    }
    /*update call permission button*/
    if(meta?.isCPR) {
      meta.buttons = CALL_PERMISSION_BUTTON;
    }
    return meta;
  };

  const containerMeta = useMemo (() => {
    let _meta = meta || DEFAULT_CONATINER_META;
    if(meta && typeof meta === "string") {
      try {
        _meta = JSON.parse(meta);
      }
      catch {
      /** add logs for future or some error notification */
        _meta =  DEFAULT_CONATINER_META;
      }
    }
    return updatePreviewVariables(_meta);
  }, [meta, config]);

  const isOrderDetails = useMemo (() => {
    if (Array.isArray(containerMeta.buttons) && containerMeta.buttons.length) {
      return containerMeta?.buttons?.some((button) => button.type === BUTTON_LIST_TYPES.ORDER_DETAILS);
    }
    return false;
  }, [templateObj]);

  const showMessagePreview = useMemo (() => {
    return containerMeta?.buttons ? containerMeta?.buttons?.length > MAX_BUTTON_INDEX : false;
  }, [templateObj]);

  const isCarouselCard = useMemo (() => {
    return templateType === TEMPLATE_TYPE.CAROUSEL;
  }, [templateObj]);

  const isAuthentication = useMemo (() => {
    if (category === CATEGORY_TYPES.AUTHENTICATION && Array.isArray(containerMeta.buttons) && containerMeta.buttons.length) {
      return true;
    }
    return false;
  }, [templateObj]);

  const isZeroTap = useMemo (() => {
    if (category === CATEGORY_TYPES.AUTHENTICATION && Array.isArray(containerMeta.buttons) && containerMeta.buttons.length) {
      return containerMeta?.buttons?.some((button) => button.type === BUTTON_LIST_TYPES.OTP && button.otp_type === BUTTON_LIST_TYPES.ZERO_TAP);
    }
    return false;
  }, [templateObj]);

  const isPasskey = useMemo (() => {
    if (category === CATEGORY_TYPES.AUTHENTICATION && Array.isArray(containerMeta.buttons) && containerMeta.buttons.length) {
      return containerMeta?.buttons?.some((button) => button.type === BUTTON_LIST_TYPES.PASSKEY);
    }
    return false;
  }, [templateObj]);

  const toggledPreview = (e) => {
    e.stopPropagation();
    setIsToggledPreview(!isToggledPreview);
  };

  const getContainerClassname = () => {
    let _class = styles.previewContainer;
    if(showMessagePreview) {
      _class += " " + styles.msgPreviewContainer;
    }
    if (isScrollOnHover) {
      _class += " " +  styles.scrollOnHover;
    }
    if (isToggledPreview && showMessagePreview) {
      _class +=  " "  + styles.disabledPreview;
    }
    return _class;
  };

  return (
    <Grid
      overflow  = "hidden"
      position  = "relative"
      width     = {width || "400px"}
      height    = {height || "auto"}
      className = {`${styles.previewRoot} ${isInPreview ? styles.inPreviewPanel : ""}`}
    >
      {showMessagePreview &&
        <Grid className = {styles.tooglePreviewPanel}>
          <Typography className = {styles.subHeader} data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.MESSAGE_PREVIEW} >Message Preview</Typography>
          <Grid className = {styles.togglePreviewBtn} onClick = {toggledPreview} >
            <Icon
              showCursor
              size       = {16}
              className  = "Message Preview Button"
              dataTestId = {TEMPLATE_PREVIEW_DATA_TESTID.MESSAGE_PREVIEW_BUTTON}
              customPath = {`${PREVIEW_ICONS}${isToggledPreview ? 'close' : 'play'}_preview.svg`}
            />
          </Grid>
        </Grid>
      }
      {isToggledPreview && showMessagePreview &&
        <Grid className = {styles.buttonListPanel} data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.PREVIEW_BUTTON_LIST}>
          <Grid className = {styles.buttonListHeader}>
            <Grid className = {styles.optionHeader} data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.ALL_OPTIONS}>All Options</Grid>
          </Grid>
          <ButtonPanel
            showAll = {true}
            buttons = {containerMeta?.buttons || []}
          />
        </Grid>
      }
      <Grid className = {getContainerClassname()}>
        {showHeading &&
          <Grid container alignItems = "center" gap = {1} sx = {{ pb: 3 }}>
            <Typography className = {styles.header} data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.MAIN_HEADING}>
              {headingName ?? "Preview"}
            </Typography>
            {isAuthentication &&
              <Tooltip placement = "top" title = "Please note that our preview tool currently displays authentication content only in English. However, the translated content will be delivered to your user on WhatsApp in your chosen language." data-testid = {createTemplateTestId.previewToolCurrently}>
                <Grid display = "flex">
                  <Icon
                    showCursor
                    size       = {20}
                    customPath = {TEMPLATE_ICONS.WARNING_CIRCLE}
                    dataTestId = {createTemplateTestId.interactiveMessage}
                  />
                </Grid>
              </Tooltip>
            }
          </Grid>
        }
        <Grid
          className   = {styles.body}
          data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.BODY_PANEL}
          sx          = {{
            direction: templateObj.languageCode === "ar" ? "rtl" : "ltr"
          }}>
          {isOrderDetails &&
            <OrderDetails
              mediaUrl            = {containerMeta?.mediaUrl}
              bypassMediaUrlCheck = {bypassMediaUrlCheck}
              hidePreview         = {hidePreview}
              validateMediaUrl    = {validateMediaUrl} />
          }
          {containerMeta?.isCPR &&
            <CallPermissionRequest />
          }
          {containerMeta?.buttons?.some((button) => button.type === BUTTON_LIST_TYPES.PAY_NOW) &&
            <PayNowPanel />
          }
          {!(isOrderDetails && TEMPLATE_TYPE.MEDIA.includes(templateType)) &&
            <Header
              headerType          = {templateType}
              containerMeta       = {containerMeta}
              mediaUrl            = {containerMeta?.mediaUrl}
              bypassMediaUrlCheck = {bypassMediaUrlCheck}
              hidePreview         = {hidePreview}
              validateMediaUrl    = {validateMediaUrl}
            />
          }
          {containerMeta?.limitedTimeOffer &&
            <LimitedTimeOffer
              buttons          = {containerMeta?.buttons || []}
              limitedTimeOffer = {containerMeta.limitedTimeOffer}
            />
          }
          {isPasskey &&
            <Passkey />
          }
          <Content
            content    = {containerMeta.sampleText || data}
            dataTestId = {TEMPLATE_PREVIEW_DATA_TESTID.BODY}
          />
          {containerMeta?.footer &&
            <Typography className = {styles.footer} data-testid = {TEMPLATE_PREVIEW_DATA_TESTID.FOOTER}>
              {containerMeta.footer}
            </Typography>
          }
          <TimeStamp timezone = {timezone} />
          {containerMeta?.buttons && !isCarouselCard && !isZeroTap && !isOrderDetails &&
            <ButtonPanel
              buttons     = {containerMeta?.buttons}
              showAll     = {false}
              isInPreview = {isInPreview}
            />
          }
        </Grid>
        {containerMeta?.buttons && isOrderDetails &&
          <ButtonPanel
            buttons        = {containerMeta?.buttons}
            showAll        = {false}
            isInPreview    = {isInPreview}
            isOrderDetails = {isOrderDetails}
          />
        }
        {isCarouselCard &&
          <CarouselCardPanel
            isInPreview         = {isInPreview}
            containerMeta       = {containerMeta}
            isScrollOnHover     = {isScrollOnHover}
            bypassMediaUrlCheck = {bypassMediaUrlCheck}
            hidePreview         = {hidePreview}
            validateMediaUrl    = {validateMediaUrl}
          />
        }
      </Grid>
    </Grid>
  );
};

export default WhatsAppPreview;
